/*****************************************************
 * Packages
 ******************************************************/
import ReactGA from 'react-ga';
import { Helmet } from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useCallback, useState, useMemo } from 'react';
/*****************************************************
 * Local
 ******************************************************/
import Card from '../../../Card';
import Alert from '../../../Alert';
import Modal from '../../../Modal';
import Button from '../../../Button';
import {
    Column,
    PlanInfo,
    FeatureList,
    ModalFooter,
    PricingBrief,
    UpdateModalBody,
} from './Billing.stc';
/*****************************************************
 * Actions
 ******************************************************/
import graphFetch from '../../../../util/graphFetch';
import dateFormat from '../../../../util/dateFormat';
import usePrevious from '../../../../hooks/usePrevious';
import { getAuthUser } from '../../../../redux/selectors';
import {
    refreshToken,
    fetchMembership,
    clearUserMessage,
    updateMembership,
} from '../../../../redux/actions';
import {
    getSuccessMessage,
    getErrorMessage,
} from '../../../../redux/selectors/user';

const featureList = {
    free: [
        { label: '1 Published Website', active: true },
        { label: '.dorik.io Subdomain', active: true },
        { label: 'Free Templates', active: true },
        { label: 'Remove Branding', active: false },
        { label: 'Integrations', active: false },
        { label: 'SEO & Social Settings', active: false },
        { label: 'Custom CSS', active: false },
        { label: 'Embed Payments', active: false },
        { label: 'Contact Form', active: false },
        { label: 'Subscription Form', active: false },
        { label: 'Custom Form', active: false },
        { label: 'Export HTML/CSS/JS', active: false },
    ],
    starter: [
        { label: 'Connect 2 Custom Domains', active: true },
        { label: '∞ Published Websites*', active: true },
        { label: 'Premium Templates', active: true },
        { label: 'Remove Branding', active: true },
        { label: 'Integrations', active: true },
        { label: 'SEO & Social Settings', active: true },
        { label: 'Custom CSS', active: true },
        { label: 'Embed Payments (soon)', active: true },
        { label: 'Contact Form', active: true },
        { label: 'Subscription Form', active: true },
        { label: 'Custom Form (soon)', active: false },
        { label: 'Export HTML/CSS/JS', active: false },
    ],
    premium: [
        { label: 'Connect 5 Custom Domains', active: true },
        { label: '∞ Published Websites*', active: true },
        { label: 'Premium Templates', active: true },
        { label: 'Remove Branding', active: true },
        { label: 'Integrations', icon: ['fas', 'times-circle'], active: true },
        { label: 'SEO & Social Settings', active: true },
        { label: 'Custom CSS', icon: ['fas', 'times-circle'], active: true },
        { label: 'Embed Payments (soon)', active: true },
        { label: 'Contact Form', icon: ['fas', 'times-circle'], active: true },
        { label: 'Subscription Form', active: true },
        {
            label: 'Custom Form (soon)',
            icon: ['fas', 'times-circle'],
            active: true,
        },
        { label: 'Export HTML/CSS/JS', active: true },
    ],
};

function Billing(props) {
    // Hooks
    const dispatch = useDispatch();
    const [info, setInfo] = useState();
    const user = useSelector(getAuthUser);
    const [loading, setLoading] = useState(false);
    const errorMsg = useSelector(getErrorMessage);
    const successMsg = useSelector(getSuccessMessage);
    const prevPlan = usePrevious(user?.membership?.planId);
    const [isUpdateModal, setIsUpdateModal] = useState(false);
    const [successPurchaseMsg, setSuccessPurchaseMsg] = useState();

    // Variables
    const ourPlans = useMemo(() => [0, 591501, 592743, 599617], []);
    const currentPlan = user?.membership?.planId;
    let currentPlanId = ourPlans.indexOf(currentPlan);
    let currentPlanName = 'FREE';
    // refactor how we determine current plan
    if (currentPlanId !== 3 && user?.membership?.status === 'deleted') {
        currentPlanId = 0;
    }
    if (currentPlanId === 1) {
        currentPlanName = 'STARTER';
    } else if (currentPlanId === 2) {
        currentPlanName = 'PREMIUM';
    } else if (currentPlanId === 3) {
        currentPlanName = 'LIFETIME';
    }

    // Functions
    const getUpdateMembershipInfo = useCallback(
        (planId) => {
            (async function () {
                const {
                    membership: { subscriptionId },
                } = user;
                const options = {
                    queryName: 'getUpdateMembershipInfo',
                    args: {
                        planId: { value: planId, type: 'Int!' },
                        subscriptionId: { value: subscriptionId, type: 'Int!' },
                    },
                    select: `
                        nextPayment {
                            date amount currency
                        }
                        immediatePayment {
                            date amount currency
                        }
                    `,
                };
                const result = await graphFetch(options);
                if (result?.data?.getUpdateMembershipInfo) {
                    setInfo(result.data.getUpdateMembershipInfo);
                    setIsUpdateModal(true);
                    dispatch(refreshToken());
                }
            })();
        },
        [user, dispatch]
    );

    const handleUpdateMembership = useCallback(
        (updateId) => {
            const planId = ourPlans[updateId];
            const {
                membership: { subscriptionId },
            } = user;
            dispatch(updateMembership({ planId, subscriptionId }));
        },
        [dispatch, user, ourPlans]
    );

    const getMembershipData = useCallback(() => dispatch(fetchMembership()), [
        dispatch,
    ]);

    const checkoutComplete = useCallback(
        (data) => {
            setLoading(false);
            const Paddle = window.Paddle;
            const checkoutId = data.checkout.id;

            Paddle.Order.details(checkoutId, function (data) {
                // TODO: show data
                setSuccessPurchaseMsg('Membership upgraded successfully!');
                setTimeout(() => {
                    dispatch(fetchMembership());
                    dispatch(refreshToken());
                }, 500);
                // Track purchase
                const { total } = data.order;
                ReactGA.event({
                    category: 'User',
                    label: data.state,
                    action: 'Purchase',
                    value: parseFloat(total),
                });
            });
        },
        [dispatch]
    );

    const handleCloseCheckout = useCallback(() => {
        setLoading(false);
        ReactGA.event({
            category: 'User',
            action: 'Closed Payment Modal',
        });
    }, []);

    const openCheckout = useCallback(
        (upgradeId) => {
            setLoading(true);
            if (currentPlanId === upgradeId) return;
            const productId = ourPlans[upgradeId];
            if (currentPlanId !== 0) {
                getUpdateMembershipInfo(productId);
            } else {
                const Paddle = window.Paddle;
                Paddle.Checkout.open({
                    email: user.email,
                    product: productId,
                    allowQuantity: false,
                    passthrough: user.id,
                    successCallback: checkoutComplete,
                    closeCallback: handleCloseCheckout,
                });
                ReactGA.event({
                    category: 'User',
                    action: 'Open Checkout',
                });
            }
        },
        [
            user.id,
            ourPlans,
            user.email,
            currentPlanId,
            checkoutComplete,
            handleCloseCheckout,
            getUpdateMembershipInfo,
        ]
    );

    const getButtonText = useCallback(
        (upgradeId) => {
            if (upgradeId > currentPlanId) return 'Upgrade Now';
            else if (upgradeId === currentPlanId) return 'Current Plan';
            else if (currentPlanId > upgradeId) return 'Contact Support';
        },
        [currentPlanId]
    );

    const handleModalClose = useCallback(() => {
        setLoading(false);
        setIsUpdateModal(false);
    }, []);

    // Side Effects
    useEffect(() => {
        getMembershipData();

        return () => dispatch(clearUserMessage());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); /* componentDidMount */

    return (
        <div className="container">
            <Helmet>
                <title>Plans & Billing</title>
            </Helmet>
            <div className="flex">
                <div className="col-md-1/1">
                    <Card
                        size="lg"
                        header={
                            <h3 style={{ fontWeight: 500 }}>Upgrade Plan</h3>
                        }
                    >
                        {successPurchaseMsg && (
                            <Alert type="success" className="mb-15">
                                {successPurchaseMsg}
                            </Alert>
                        )}
                        <MembershipInfo
                            planId={currentPlanId}
                            membership={user.membership}
                            getMembershipData={getMembershipData}
                        >
                            <span>
                                Your current plan is{' '}
                                <strong>{currentPlanName}</strong>
                            </span>
                        </MembershipInfo>
                        <div className="flex">
                            <div className="col-md-1/3">
                                <Column className="column-inner">
                                    <PricingBrief>
                                        <span className="package-name">
                                            BASIC
                                        </span>
                                        <div className="price-wrap">
                                            <strong className="price">
                                                FREE
                                            </strong>
                                        </div>
                                        <span className="price-meta">N/A</span>
                                        <Button
                                            fullWidth
                                            size="2x"
                                            type="primary-alt"
                                            disabled={
                                                currentPlanId >= 0 || loading
                                            }
                                            title={
                                                currentPlanId > 0
                                                    ? 'Contact support to downgrade'
                                                    : ''
                                            }
                                        >
                                            {getButtonText(0)}
                                        </Button>
                                    </PricingBrief>

                                    <FeatureList>
                                        {featureList.free.map((item, i) => (
                                            <li
                                                key={i}
                                                className={
                                                    item.active ? 'active' : ''
                                                }
                                            >
                                                {item.active ? (
                                                    <FontAwesomeIcon
                                                        fixedWidth
                                                        icon={[
                                                            'fas',
                                                            'check-circle',
                                                        ]}
                                                    />
                                                ) : (
                                                    <FontAwesomeIcon
                                                        fixedWidth
                                                        icon={[
                                                            'fas',
                                                            'times-circle',
                                                        ]}
                                                    />
                                                )}
                                                {item.label}
                                            </li>
                                        ))}
                                    </FeatureList>
                                </Column>
                            </div>

                            <div className="col-md-1/3">
                                <Column className="column-inner">
                                    <PricingBrief>
                                        <span className="package-name">
                                            STARTER
                                        </span>
                                        <div className="price-wrap">
                                            <span className="prefix">$</span>
                                            <strong className="price">3</strong>
                                            <span className="suffix">
                                                / month
                                            </span>
                                        </div>

                                        <span className="price-meta">
                                            Billed <strong>$36</strong> Annually
                                        </span>

                                        <Button
                                            fullWidth
                                            size="2x"
                                            type="primary-alt"
                                            disabled={
                                                currentPlanId >= 1 || loading
                                            }
                                            onClick={() => openCheckout(1)}
                                            title={
                                                currentPlanId > 1
                                                    ? 'Contact support to downgrade'
                                                    : ''
                                            }
                                        >
                                            {getButtonText(1)}
                                        </Button>
                                    </PricingBrief>
                                    <FeatureList>
                                        {featureList.starter.map((item, i) => (
                                            <li
                                                key={i}
                                                className={
                                                    item.active ? 'active' : ''
                                                }
                                            >
                                                {item.active ? (
                                                    <FontAwesomeIcon
                                                        fixedWidth
                                                        icon={[
                                                            'fas',
                                                            'check-circle',
                                                        ]}
                                                    />
                                                ) : (
                                                    <FontAwesomeIcon
                                                        fixedWidth
                                                        icon={[
                                                            'fas',
                                                            'times-circle',
                                                        ]}
                                                    />
                                                )}
                                                {item.label}
                                            </li>
                                        ))}
                                    </FeatureList>
                                </Column>
                            </div>

                            <div className="col-md-1/3">
                                <Column className="column-inner">
                                    <PricingBrief>
                                        <span className="package-name">
                                            PREMIUM
                                        </span>
                                        <div className="price-wrap">
                                            <span className="prefix">$</span>
                                            <strong className={`price`}>
                                                5
                                            </strong>
                                            <span className="suffix">
                                                / month
                                            </span>
                                        </div>

                                        <span className="price-meta">
                                            Billed <strong>$60</strong> Annually
                                        </span>
                                        <Button
                                            fullWidth
                                            size="2x"
                                            type="primary-alt"
                                            disabled={
                                                currentPlanId >= 2 || loading
                                            }
                                            onClick={() => openCheckout(2)}
                                        >
                                            {getButtonText(2)}
                                        </Button>
                                    </PricingBrief>
                                    <FeatureList>
                                        {featureList.premium.map((item, i) => (
                                            <li
                                                key={i}
                                                className={
                                                    item.active ? 'active' : ''
                                                }
                                            >
                                                {item.active ? (
                                                    <FontAwesomeIcon
                                                        fixedWidth
                                                        icon={[
                                                            'fas',
                                                            'check-circle',
                                                        ]}
                                                    />
                                                ) : (
                                                    <FontAwesomeIcon
                                                        fixedWidth
                                                        icon={[
                                                            'fas',
                                                            'times-circle',
                                                        ]}
                                                    />
                                                )}
                                                {item.label}
                                            </li>
                                        ))}
                                    </FeatureList>
                                </Column>
                            </div>
                        </div>

                        <p style={{ textAlign: 'center', marginTop: 20 }}>
                            7-Day Money-Back Guarantee. <br />
                            All prices are exclusive of VAT or sales tax. Any
                            applicable tax will be added on checkout.
                        </p>
                    </Card>
                </div>
            </div>

            {isUpdateModal && (
                <Modal
                    size="sm"
                    dropShadow
                    header="Update Info"
                    onClose={handleModalClose}
                    displayName="UpdateMembership"
                    footer={
                        <ModalFooter>
                            {/* TODO: send the plan id dynamically */}
                            {currentPlan !== prevPlan ? (
                                <div />
                            ) : (
                                <Button
                                    onClick={() => handleUpdateMembership(2)}
                                >
                                    Upgrade Now
                                </Button>
                            )}
                            <Button type="link" onClick={handleModalClose}>
                                Close
                            </Button>
                        </ModalFooter>
                    }
                >
                    <UpdateModalBody>
                        {successMsg && (
                            <Alert type="success">{successMsg}</Alert>
                        )}
                        {errorMsg && <Alert type="danger">{errorMsg}</Alert>}
                        {info?.immediatePayment?.amount > 0 ? (
                            <p>
                                You will be billed{' '}
                                <strong>
                                    US${info?.immediatePayment?.amount}
                                </strong>{' '}
                                on{' '}
                                <strong>
                                    {dateFormat(info?.immediatePayment?.date)}
                                </strong>{' '}
                                and{' '}
                                <strong>US${info?.nextPayment?.amount}</strong>{' '}
                                every 1 year after.
                            </p>
                        ) : (
                            <p>
                                You will have a credit balance of{' '}
                                <strong>
                                    US$
                                    {Math.abs(info?.immediatePayment?.amount)}
                                </strong>
                                . Once the credit balance is used up, your
                                recurring billing amount will be{' '}
                                <strong>US${info?.nextPayment?.amount}</strong>{' '}
                                every 1 year.
                            </p>
                        )}
                    </UpdateModalBody>
                </Modal>
            )}
        </div>
    );
}

export default Billing;

function MembershipInfo({ planId, membership, children, getMembershipData }) {
    const dispatch = useDispatch();

    const handleClose = useCallback(() => {
        setTimeout(() => {
            dispatch(refreshToken());
            getMembershipData();
        }, 500);
    }, [dispatch, getMembershipData]);

    const openCheckout = useCallback(
        (url) => {
            const Paddle = window.Paddle;
            Paddle.Checkout.open({
                override: url,
                closeCallback: handleClose,
            });
        },
        [handleClose]
    );

    if (!membership) return null;
    const {
        status,
        renewAt,
        cancelUrl,
        updateUrl,
        cancellationEffectiveDate,
    } = membership;

    return (
        <PlanInfo>
            <>
                {children}

                {status === 'deleted' && (
                    <>
                        <span>
                            Active Until:{' '}
                            <strong>
                                {dateFormat(cancellationEffectiveDate)}
                            </strong>
                        </span>{' '}
                    </>
                )}
                {status === 'active' && (
                    <>
                        <span>
                            Renews On: <strong>{dateFormat(renewAt)}</strong>
                        </span>{' '}
                    </>
                )}
                {status === 'active' ? (
                    <div className="push-right">
                        {/* <Button
                            type="link"
                            onClick={() => openCheckout(cancelUrl)}
                        >
                            Cancel Plan
                        </Button> */}
                        <Button
                            type="primary-alt"
                            onClick={() => openCheckout(updateUrl)}
                        >
                            Update Payment Method
                        </Button>
                    </div>
                ) : null}
            </>
        </PlanInfo>
    );
}
